import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import Layout from "./components/Layout";

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy loaded pages
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const EmployeesPage = React.lazy(() => import("./pages/EmployeesPage"));
const ShiftsPage = React.lazy(() => import("./pages/ShiftsPage"));
const TimeTrackingPage = React.lazy(() => import("./pages/TimeTrackingPage"));
const SubscriptionPage = React.lazy(() => import("./pages/SubscriptionPage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const RulesPage = React.lazy(() => import("./pages/RulesPage"));
const TemplatesPage = React.lazy(() => import("./pages/TemplatesPage"));
const IntegrationsPage = React.lazy(() => import("./pages/IntegrationsPage"));
const SwapsPage = React.lazy(() => import("./pages/SwapsPage"));
const AnalyticsPage = React.lazy(() => import("./pages/AnalyticsPage"));
const AISchedulingPage = React.lazy(() => import("./pages/AISchedulingPage"));
const GeofencingPage = React.lazy(() => import("./pages/GeofencingPage"));
const ReportsPage = React.lazy(() => import("./pages/ReportsPage"));
const CRMPage = React.lazy(() => import("./pages/CRMPage"));
const ERPPage = React.lazy(() => import("./pages/ERPPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const DomainsPage = React.lazy(() => import("./pages/DomainsPage"));
const NotificationSettingsPage = React.lazy(() => import("./pages/NotificationSettingsPage"));
const MyNotificationSettingsPage = React.lazy(() => import("./pages/MyNotificationSettingsPage"));
const OrgReminderSettingsPage = React.lazy(() => import("./pages/OrgReminderSettingsPage"));
const ReminderReportsPage = React.lazy(() => import("./pages/ReminderReportsPage"));
const ComplianceAlertsPage = React.lazy(() => import("./pages/ComplianceAlertsPage"));
const CalendarOAuthSettingsPage = React.lazy(() => import("./pages/CalendarOAuthSettingsPage"));
const BroadcastPage = React.lazy(() => import("./pages/BroadcastPage"));
const MobileAppPage = React.lazy(() => import("./pages/MobileAppPage"));
const CompliancePage = React.lazy(() => import("./pages/CompliancePage"));
const SecurityAlertsPage = React.lazy(() => import("./pages/SecurityAlertsPage"));
const AIAutoAssignPage = React.lazy(() => import("./pages/AIAutoAssignPage"));
const WhatsAppPage = React.lazy(() => import("./pages/WhatsAppPage"));
const AIPredictionsPage = React.lazy(() => import("./pages/AIPredictionsPage"));
const GamificationPage = React.lazy(() => import("./pages/GamificationPage"));
const GamificationSettingsPage = React.lazy(() => import("./pages/GamificationSettingsPage"));
const AdvancedAnalyticsPage = React.lazy(() => import("./pages/AdvancedAnalyticsPage"));
const MarketplacePage = React.lazy(() => import("./pages/MarketplacePage"));
const MultiLocationPage = React.lazy(() => import("./pages/MultiLocationPage"));
const ShiftSwapPage = React.lazy(() => import("./pages/ShiftSwapPage"));
const HoursDocumentsPage = React.lazy(() => import("./pages/HoursDocumentsPage"));
const LaborRulesPage = React.lazy(() => import("./pages/LaborRulesPage"));
const EmployeeCreditsPage = React.lazy(() => import("./pages/EmployeeCreditsPage"));
const PaymentSettingsPage = React.lazy(() => import("./pages/PaymentSettingsPage"));
const DigitalSignaturesPage = React.lazy(() => import("./pages/DigitalSignaturesPage"));
const DocumentTemplatesPage = React.lazy(() => import("./pages/DocumentTemplatesPage"));
const EmployeeOnboardingPage = React.lazy(() => import("./pages/EmployeeOnboardingPage"));
const ChatPage = React.lazy(() => import("./pages/ChatPage"));
const LeavePage = React.lazy(() => import("./pages/LeavePage"));
const IncentivesPage = React.lazy(() => import("./pages/IncentivesPage"));
const PayrollPage = React.lazy(() => import("./pages/PayrollPage"));
const MedicalLeavePage = React.lazy(() => import("./pages/MedicalLeavePage"));
const PerformancePage = React.lazy(() => import("./pages/PerformancePage"));
const ChangePasswordRequiredPage = React.lazy(() => import("./pages/ChangePasswordRequiredPage"));
const TwoFactorRequiredPage = React.lazy(() => import("./pages/TwoFactorRequiredPage"));
const PaymentPage = React.lazy(() => import("./pages/PaymentPage"));
const EmployeePaymentPage = React.lazy(() => import("./pages/EmployeePaymentPage"));
const PaymentHistoryPage = React.lazy(() => import("./pages/PaymentHistoryPage"));
const MyPaymentHistoryPage = React.lazy(() => import("./pages/MyPaymentHistoryPage"));
const EmployeeTrackingPage = React.lazy(() => import("./pages/EmployeeTrackingPage"));
const CEOHotelDashboardPage = React.lazy(() => import("./pages/CEOHotelDashboardPage"));
const CEOHospitalDashboardPage = React.lazy(() => import("./pages/CEOHospitalDashboardPage"));
const PropertyHierarchyPage = React.lazy(() => import("./pages/PropertyHierarchyPage"));
const MedicalServicesPage = React.lazy(() => import("./pages/MedicalServicesPage"));
const SafetyCompliancePage = React.lazy(() => import("./pages/SafetyCompliancePage"));
const IntegrationHubPage = React.lazy(() => import("./pages/IntegrationHubPage"));
const HospitalOperationsPage = React.lazy(() => import("./pages/HospitalOperationsPage"));
const HotelOperationsPage = React.lazy(() => import("./pages/HotelOperationsPage"));
const AIWorkforcePage = React.lazy(() => import("./pages/AIWorkforcePage"));
const ComplianceCertificationsPage = React.lazy(() => import("./pages/ComplianceCertificationsPage"));

// Protected Route Component
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { isAuthenticated, loading, user, isSuperAdmin, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Priority: Password Change → 2FA Setup → Dashboard
  // Check current route first to prevent infinite redirect loops
  const isOnPasswordChangeRoute = location.pathname === '/change-password-required';
  const isOn2FASetupRoute = location.pathname === '/two-factor-required';

  // If on password change route, allow access (skip other checks to prevent loops)
  if (isOnPasswordChangeRoute) {
    return <Layout>{children}</Layout>;
  }

  // If on 2FA setup route, allow access (skip password change check)
  if (isOn2FASetupRoute) {
    // Still check 2FA requirement, but don't redirect if already on the route
    return <Layout>{children}</Layout>;
  }

  // Enforce password change: redirect users with temporary passwords
  if (mustChangePassword) {
    return <Navigate to="/change-password-required" replace />;
  }

  // Enforce forced 2FA: redirect non-admin users that must set up 2FA
  const mustSetup2FA = !!user?.must_setup_2fa;
  const isAdmin = user?.role === 'super_admin' || user?.role === 'org_admin';

  if (mustSetup2FA && !isAdmin) {
    return <Navigate to="/two-factor-required" replace />;
  }

  // Check if user needs onboarding (no organization)
  if (!user?.organization_id && user?.role !== 'super_admin' && user?.role !== 'worker') {
    return <Navigate to="/onboarding" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    if (!user?.organization_id && user?.role !== 'super_admin' && user?.role !== 'worker') {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Onboarding Route
const OnboardingRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.organization_id || user?.role === 'super_admin' || user?.role === 'worker') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Landing Page - Public */}
        <Route path="/" element={<LandingPage />} />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <RouteErrorBoundary><LoginPage /></RouteErrorBoundary>
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RouteErrorBoundary><RegisterPage /></RouteErrorBoundary>
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <RouteErrorBoundary><ForgotPasswordPage /></RouteErrorBoundary>
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <RouteErrorBoundary><ResetPasswordPage /></RouteErrorBoundary>
            </PublicRoute>
          }
        />

        {/* Onboarding */}
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <RouteErrorBoundary><OnboardingPage /></RouteErrorBoundary>
            </OnboardingRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/change-password-required"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ChangePasswordRequiredPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/two-factor-required"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><TwoFactorRequiredPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><DashboardPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/new"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ShiftsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts/new"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ShiftsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/time-tracking"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><TimeTrackingPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SubscriptionPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-history"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PaymentHistoryPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-payment-history"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MyPaymentHistoryPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pay/:sessionId"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PaymentPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pay/employee/:sessionId"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeePaymentPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><DashboardPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rules"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><RulesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><TemplatesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/swaps"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SwapsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AnalyticsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-scheduling"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AISchedulingPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/geofencing"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><GeofencingPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ReportsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><CRMPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/erp"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ERPPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><NotificationsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/domains"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><DomainsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-settings"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><NotificationSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-notifications"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MyNotificationSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/org-reminders"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><OrgReminderSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reminder-reports"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ReminderReportsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance-alerts"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ComplianceAlertsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar-oauth"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><CalendarOAuthSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/broadcast"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><BroadcastPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile-app"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MobileAppPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><CompliancePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security-alerts"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SecurityAlertsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-auto-assign"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AIAutoAssignPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><WhatsAppPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-predictions"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AIPredictionsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/gamification"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><GamificationPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/gamification/settings"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><GamificationSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/advanced-analytics"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AdvancedAnalyticsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MarketplacePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MultiLocationPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shift-swap"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ShiftSwapPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hours"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><HoursDocumentsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/labor-rules"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><LaborRulesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-credits"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeeCreditsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-settings"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PaymentSettingsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/signatures"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><DigitalSignaturesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doc-templates"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><DocumentTemplatesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hire"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeeOnboardingPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ChatPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><LeavePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incentives"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><IncentivesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PayrollPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/medical-leave"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MedicalLeavePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PerformancePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee-tracking"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><EmployeeTrackingPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* CEO Dashboard Routes */}
        <Route
          path="/ceo/hotel"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><CEOHotelDashboardPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ceo/hospital"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><CEOHospitalDashboardPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/property-hierarchy"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><PropertyHierarchyPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/medical-services"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><MedicalServicesPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/safety-compliance"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SafetyCompliancePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integration-hub"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><IntegrationHubPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Hospital & Hotel Operations */}
        <Route
          path="/hospital-operations"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><HospitalOperationsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hotel-operations"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><HotelOperationsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-workforce"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><AIWorkforcePage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance-certifications"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><ComplianceCertificationsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireSuperAdmin>
              <RouteErrorBoundary><AdminPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai"
          element={
            <ProtectedRoute requireSuperAdmin>
              <RouteErrorBoundary><AdminPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/integrations"
          element={
            <ProtectedRoute requireSuperAdmin>
              <RouteErrorBoundary><IntegrationsPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Payment callbacks */}
        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SubscriptionPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/cancel"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary><SubscriptionPage /></RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <AppRoutes />
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
